import type { KeyboardEvent } from 'react';
import { useState } from 'react';
import { toast } from 'sonner';

import { CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE } from '@/entities/campaign';
import { Button } from '@/shared/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import { Textarea } from '@/shared/components/shadcn/textarea';

import type { CreatorCsvPreviewState } from '../../hooks';
import { CampaignCreatorImportPreview } from '../ui';

type Props = {
  campaignItemSlotLimit: number;
  onApplyBulkPaste: (preview: CreatorCsvPreviewState) => void;
  onGetBulkPastePreview: (text: string) => CreatorCsvPreviewState | undefined;
  remainingSlots: number;
};

export function CampaignCreatorBulkPasteDialog({
  campaignItemSlotLimit,
  onApplyBulkPaste,
  onGetBulkPastePreview,
  remainingSlots,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [preview, setPreview] = useState<CreatorCsvPreviewState>();
  const campaignCreatorLimitWarningMessage = `You can add up to ${campaignItemSlotLimit} creators. Extra entries will be ignored.`;

  const resetDialog = () => {
    setInputValue('');
    setPreview(undefined);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      resetDialog();
    }
  };

  const handleInputChange = (value: string) => {
    const previousUnsupportedPlatformCount = preview?.unsupportedPlatformCount ?? 0;
    const previousLimitSkippedCount = preview?.limitSkippedCount ?? 0;
    const nextPreview = onGetBulkPastePreview(value);

    setInputValue(value);
    setPreview(nextPreview);

    if ((nextPreview?.unsupportedPlatformCount ?? 0) > previousUnsupportedPlatformCount) {
      toast.warning(CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE);
    }
    if ((nextPreview?.limitSkippedCount ?? 0) > previousLimitSkippedCount) {
      toast.warning(campaignCreatorLimitWarningMessage);
    }
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter') return;
    if ((preview?.summary.added ?? 0) < remainingSlots && (preview?.limitSkippedCount ?? 0) === 0) {
      return;
    }

    toast.warning(campaignCreatorLimitWarningMessage);
  };

  const handleConfirm = () => {
    if (!preview || preview.summary.added === 0) return;
    onApplyBulkPaste(preview);
    handleOpenChange(false);
  };

  return (
    <>
      <Button
        type="button"
        className="md:shrink-0"
        onClick={() => setIsOpen(true)}
        disabled={remainingSlots === 0}
      >
        Bulk paste
      </Button>

      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-170 gap-6">
          <DialogHeader>
            <DialogTitle>Paste creators in bulk</DialogTitle>
            <DialogDescription>Paste usernames or profile links, one per line</DialogDescription>
          </DialogHeader>

          <Textarea
            value={inputValue}
            onChange={(event) => handleInputChange(event.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder="https://www.tiktok.com/@creator_handle&#10;https://www.instagram.com/creator_handle"
            className="min-h-45 resize-none text-sm leading-6"
          />

          <CampaignCreatorImportPreview
            count={preview?.summary.added ?? 0}
            label="Creators to add:"
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={!preview || preview.summary.added === 0}>
              Add creators
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
